import type { BalanceOverrides } from './types';
import { BALANCE } from '../data/balance';

/**
 * Résolution des réglages d'équilibrage — MVP_DESIGN §2 / DECISIONS D.8.
 *
 * `BalanceOverrides` est l'API de debug (panneau d'équilibrage) : elle n'est
 * **jamais** sauvegardée et s'applique au prochain calcul concerné. Les champs
 * se rangent en deux familles :
 *  - *remplacements* (durée d'Expédition, cooldown, croissances par jour, par
 *    niveau et de l'or, plafond de poison, croissance du coût des talents, facteur de niveau des dés et Mult_Corrupted — A111) : la valeur donnée remplace la constante de `BALANCE` ; `hpGrowth`
 *    est l'**alias historique** de `hpDayGrowth` (croissance des PV par jour) ;
 *  - *compositions* (dégâts, critique, dégâts plats, sorts, PV/or des ennemis,
 *    coût des talents et des dés, glissement des raretés) :
 *    la valeur se compose avec les stats dérivées (× pour les multiplicateurs,
 *    + pour les additions), et vaut donc « neutre » par défaut.
 *
 * Fonction pure : aucun état, aucun cache global.
 */
export interface ResolvedBalance {
  /** Durée de base d'une Expédition, avant le nœud *Journée longue* (s). */
  expeditionDuration: number;
  /** Durée d'Invasion = durée **de base** d'Expédition × ce facteur, plus le bonus de nœud (A101). */
  invasionDurationFactor: number;
  /** Temps de retour de base, avant le nœud *Bras rapide* (s). */
  throwCooldown: number;
  /** Multiplicateur de dégâts **supplémentaire** (1 = neutre). */
  damageMult: number;
  /** Chance de critique **ajoutée** au lancer (0 = neutre). */
  critChance: number;
  /** Multiplicateur de critique **ajouté** (0 = neutre). */
  critMult: number;
  /** Croissance des PV d'un **jour** à l'autre dans un niveau (D = 1…6). */
  hpDayGrowth: number;
  /** Croissance des PV d'un **niveau d'expédition** à l'autre (L). */
  hpLevelGrowth: number;
  /**
   * Multiplicateur de PV **cumulé** par niveau (index 0 = niveau 1).
   * Vide → croissance géométrique pure en `hpLevelGrowth^(L−1)`, comme avant la
   * v0.5.1 ; c'est le cas dès qu'une surcharge de debug fixe `hpLevelGrowth`.
   */
  hpLevelCurve: readonly number[];
  /** Croissance de l'or par vague. */
  goldGrowth: number;
  /** Croissance de l'or d'un **niveau d'Expédition** à l'autre (A24 ; 1 = neutre). */
  goldLevelGrowth: number;
  /** Dégâts plats **ajoutés** à chaque dé (0 = neutre). */
  flatDamagePerDie: number;
  /** Multiplicateur **supplémentaire** de puissance des sorts (1 = neutre). */
  spellPower: number;
  /** Plafond de la somme des affixes *Empoisonné* d'un lancer. */
  poisonCap: number;
  /** Multiplicateur de PV appliqué aux ennemis à leur apparition (1 = neutre). */
  enemyHpMult: number;
  /** Multiplicateur d'or appliqué aux ennemis à leur apparition (1 = neutre). */
  enemyGoldMult: number;
  /** Croissance du coût d'un niveau de talent (`coût = base × mult × croissance^niveau`). */
  nodeCostGrowth: number;
  /** Multiplicateur des coûts de base des talents (1 = neutre). */
  nodeCostMult: number;
  /** Multiplicateur du prix d'un dé au Gachapon (1 = neutre). */
  machineCostMult: number;
  /** Part des poids de rareté glissée d'un cran vers le bas (0 = neutre). */
  rarityShift: number;
  /** Facteur de dégâts par niveau de dé (Mult_Niveau), `BALANCE.dieLevelFactor` par défaut (A111). */
  dieLevelFactor: number;
  /** Multiplicateur des dés Corrupted (Mult_Corrupted), `BALANCE.corruptedFlatFactor` par défaut (A111). */
  corruptedFlatFactor: number;
}

/** Nombre fini, sinon la valeur par défaut (un panneau de debug peut envoyer n'importe quoi). */
function pick(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Applique des surcharges de debug sur les constantes du jeu. */
export function resolveBalance(o: BalanceOverrides = {}): ResolvedBalance {
  return {
    expeditionDuration: pick(o.expeditionDuration, BALANCE.expeditionDuration),
    invasionDurationFactor: pick(o.invasionDurationFactor, BALANCE.invasionDurationFactor),
    throwCooldown: pick(o.throwCooldown, BALANCE.throwCooldown),
    damageMult: pick(o.damageMult, 1),
    critChance: pick(o.critChance, 0),
    critMult: pick(o.critMult, 0),
    // `hpGrowth` est l'ancien nom (croissance par vague) : il est conservé comme
    // **alias** de la croissance par jour pour ne pas casser le panneau de debug
    // et les sauvegardes d'export d'équilibrage (COMMENTS v0.3.0 §3.2).
    hpDayGrowth: pick(o.hpDayGrowth, pick(o.hpGrowth, BALANCE.hpDayGrowth)),
    hpLevelGrowth: pick(o.hpLevelGrowth, BALANCE.hpLevelGrowth),
    // Une surcharge explicite de `hpLevelGrowth` (panneau de debug, table de
    // sensibilité) reprend la main sur la table : elle redevient géométrique.
    hpLevelCurve:
      typeof o.hpLevelGrowth === 'number' && Number.isFinite(o.hpLevelGrowth)
        ? []
        : BALANCE.hpLevelCurve,
    goldGrowth: pick(o.goldGrowth, BALANCE.goldGrowth),
    goldLevelGrowth: pick(o.goldLevelGrowth, BALANCE.goldLevelGrowth),
    flatDamagePerDie: pick(o.flatDamagePerDie, 0),
    spellPower: pick(o.spellPower, 1),
    poisonCap: pick(o.poisonCap, BALANCE.poisonCap),
    enemyHpMult: pick(o.enemyHpMult, 1),
    enemyGoldMult: pick(o.enemyGoldMult, 1),
    // Phase 6 (balayage du simulateur) : leviers d'économie. Neutres par
    // défaut — sans surcharge, chaque coût est identique au bit près.
    nodeCostGrowth: pick(o.nodeCostGrowth, BALANCE.nodeCostGrowth),
    nodeCostMult: pick(o.nodeCostMult, 1),
    machineCostMult: pick(o.machineCostMult, 1),
    rarityShift: Math.min(1, Math.max(0, pick(o.rarityShift, 0))),
    // A111 : les deux paramètres du carnet des dés Corrupted, surchargeables en debug.
    dieLevelFactor: pick(o.dieLevelFactor, BALANCE.dieLevelFactor),
    corruptedFlatFactor: pick(o.corruptedFlatFactor, BALANCE.corruptedFlatFactor),
  };
}

/** Réglages du jeu sans aucune surcharge (valeur par défaut de tous les appels). */
export const DEFAULT_BALANCE: ResolvedBalance = resolveBalance();

/** `true` si l'objet contient au moins une surcharge exploitable. */
export function hasOverrides(o: BalanceOverrides): boolean {
  return Object.values(o).some((v) => typeof v === 'number' && Number.isFinite(v));
}
